import { useCallback, useEffect, useState } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Loader2,
  FileText,
  Check,
  X,
  AlertTriangle,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const STATUS_TABS = [
  { id: "pending_review", label: "Pendientes" },
  { id: "approved", label: "Aprobados" },
  { id: "rejected", label: "Rechazados" },
  { id: "expired", label: "Vencidos" },
  { id: "fraud_flagged", label: "Sospechosos" },
];

const STATUS_COLOR = {
  pending_review: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-neutral-100 text-neutral-700",
  fraud_flagged: "bg-purple-50 text-purple-700",
};

function StatPill({ icon: Icon, label, value, accent }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${accent}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div>
        <p className="text-[11px] uppercase tracking-wider opacity-75">{label}</p>
        <p className="font-heading font-bold text-lg tabular-nums">{value}</p>
      </div>
    </div>
  );
}

export default function AdminInsuranceTab() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("pending_review");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null);
  const [actionType, setActionType] = useState(null); // approve | reject | flag_fraud
  const [actionReason, setActionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/insurance/stats");
      setStats(data);
    } catch (e) {
      /* silent */
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/insurance", {
        params: { status, page, page_size: 25, q: q || undefined },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Error al cargar pólizas");
    } finally {
      setLoading(false);
    }
  }, [status, page, q]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openAction = (rec, type) => {
    setSelected(rec);
    setActionType(type);
    setActionReason("");
  };

  const submitAction = async () => {
    if (!selected || !actionType) return;
    if ((actionType === "reject" || actionType === "flag_fraud") && !actionReason.trim()) {
      toast.error("El motivo es requerido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/insurance/${selected.record_id}/action`, {
        action: actionType,
        reason: actionReason.trim() || undefined,
      });
      toast.success(
        actionType === "approve"
          ? "Póliza aprobada"
          : actionType === "reject"
            ? "Póliza rechazada"
            : "Marcada para revisión",
      );
      setSelected(null);
      setActionType(null);
      await fetchStats();
      await fetchList();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-insurance-tab">
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-terracotta to-amber-500 items-center justify-center text-white">
            <Shield className="w-4 h-4" />
          </span>
          <h2 className="font-heading font-bold text-xl text-agave">
            Verificación de Seguros
          </h2>
        </div>
        <p className="text-sm text-neutral-500 mt-1">
          Revisa, aprueba o rechaza pólizas de Seguro Multiplataforma.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatPill icon={Clock} label="Pendientes" value={stats.pending} accent="bg-amber-50 border-amber-200 text-amber-700" />
          <StatPill icon={ShieldCheck} label="Aprobados" value={stats.approved} accent="bg-emerald-50 border-emerald-200 text-emerald-700" />
          <StatPill icon={AlertTriangle} label="Vencen <30d" value={stats.expiring_soon} accent="bg-orange-50 border-orange-200 text-orange-700" />
          <StatPill icon={ShieldAlert} label="Vencidos" value={stats.expired} accent="bg-neutral-100 border-neutral-300 text-neutral-700" />
          <StatPill icon={X} label="Rechazados" value={stats.rejected} accent="bg-red-50 border-red-200 text-red-700" />
          <StatPill icon={ShieldAlert} label="Sospechosos" value={stats.fraud_flagged} accent="bg-purple-50 border-purple-200 text-purple-700" />
        </div>
      )}

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <TabsList className="bg-neutral-100">
            {STATUS_TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className="text-xs sm:text-sm" data-testid={`insurance-tab-${t.id}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-md ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Buscar por nombre, aseguradora, póliza…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchList(); } }}
            className="pl-9"
            data-testid="insurance-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 text-sm">
            No hay pólizas con este estado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Anfitrión</TableHead>
                  <TableHead>Aseguradora</TableHead>
                  <TableHead>Póliza</TableHead>
                  <TableHead>Vence</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.record_id} data-testid={`insurance-row-${row.record_id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">
                        {row.host?.first_name} {row.host?.last_name}
                      </p>
                      <p className="text-xs text-neutral-500">{row.host?.email}</p>
                    </TableCell>
                    <TableCell className="text-sm">{row.insurance_company}</TableCell>
                    <TableCell className="text-sm font-mono">{row.policy_number}</TableCell>
                    <TableCell className="text-sm">
                      {row.expiration_date}
                      {typeof row.days_until_expiry === "number" && (
                        <p className={`text-xs ${row.days_until_expiry < 0 ? "text-red-600" : row.days_until_expiry < 30 ? "text-amber-600" : "text-neutral-400"}`}>
                          {row.days_until_expiry < 0
                            ? `${Math.abs(row.days_until_expiry)}d vencido`
                            : `${row.days_until_expiry}d`}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR[row.status] || "bg-neutral-100 text-neutral-700"}`}>
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelected(row)}
                        data-testid={`insurance-view-${row.record_id}`}
                      >
                        Revisar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {total > 25 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-neutral-500">
            {total.toLocaleString()} registros · Página {page}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
          </div>
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setActionType(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Revisar póliza</DialogTitle>
            <DialogDescription>
              Verifica los datos contra el documento subido.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <p><span className="text-neutral-500">Anfitrión:</span> <strong>{selected.host?.first_name} {selected.host?.last_name}</strong></p>
                <p><span className="text-neutral-500">Email:</span> {selected.host?.email}</p>
                <p><span className="text-neutral-500">Titular:</span> {selected.owner_name}</p>
                <p><span className="text-neutral-500">Aseguradora:</span> {selected.insurance_company}</p>
                <p><span className="text-neutral-500">Póliza:</span> <strong>{selected.policy_number}</strong></p>
                <p><span className="text-neutral-500">Vence:</span> {selected.expiration_date}</p>
              </div>

              {selected.rejection_reason && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm">
                  <p className="font-semibold text-red-700">Rechazo previo</p>
                  <p className="text-neutral-700">{selected.rejection_reason}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold mb-2">Documentos</p>
                <div className="space-y-2">
                  {(selected.documents || []).map((d, i) => (
                    <a
                      key={i}
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 hover:border-terracotta hover:bg-terracotta/5"
                      data-testid={`insurance-doc-link-${i}`}
                    >
                      <FileText className="w-5 h-5 text-terracotta" />
                      <p className="text-sm flex-1 truncate">{d.url.split("/").pop()}</p>
                      <span className="text-xs text-terracotta">Abrir</span>
                    </a>
                  ))}
                </div>
              </div>

              {actionType === "reject" || actionType === "flag_fraud" ? (
                <div>
                  <p className="text-sm font-semibold mb-2">
                    {actionType === "reject" ? "Motivo del rechazo" : "Motivo de sospecha"}
                  </p>
                  <Textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Ej: La póliza no muestra cobertura comercial / nombre no coincide…"
                    rows={3}
                    data-testid="insurance-reason-textarea"
                  />
                </div>
              ) : null}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {actionType ? (
              <>
                <Button variant="outline" onClick={() => setActionType(null)} disabled={submitting}>
                  Volver
                </Button>
                <Button
                  onClick={submitAction}
                  disabled={submitting}
                  className={actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  data-testid="insurance-confirm-action"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Confirmar {actionType === "approve" ? "aprobación" : actionType === "reject" ? "rechazo" : "marcado"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => openAction(selected, "flag_fraud")} data-testid="insurance-flag-btn">
                  <ShieldAlert className="w-4 h-4 mr-2" /> Marcar sospecha
                </Button>
                <Button variant="outline" onClick={() => openAction(selected, "reject")} className="text-red-600" data-testid="insurance-reject-btn">
                  <X className="w-4 h-4 mr-2" /> Rechazar
                </Button>
                <Button onClick={() => openAction(selected, "approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="insurance-approve-btn">
                  <Check className="w-4 h-4 mr-2" /> Aprobar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
